import noop from 'lib/noop'
import React, { useCallback, useContext, useRef } from 'react'
import { InView } from 'react-intersection-observer'
import OfferListEventsContext, { OfferListEvents } from './OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import HeroExperienceOfferTile from '../OfferListTiles/HeroOfferTiles/HeroExperienceOfferTile'
import useExperience from 'hooks/Experiences/useExperience'
import HeroOfferLoadingTile from 'home/components/OfferCarousels/common/HeroOfferLoadingTile'

interface Props {
  experienceId: string;
  position: number;
  className?: string;
  onInViewChange?: (inView: boolean) => void;
  /**
   * The % of the tile that needs to be in view before being considered 'in view'
   * Value between 0 and 1 (e.g. 0.1 = 10% of it)
   * */
  inViewThreshhold?: number;
}

/**
 * Hero offer list tile that will render the experience tile
 */
function OfferListExperienceTile(props: Props) {
  const {
    experienceId,
    position,
    className,
    inViewThreshhold = 0.5,
    onInViewChange = noop,
  } = props

  const fireOfferListEvent = useContext(OfferListEventsContext) ?? noop
  const [experience] = useExperience(experienceId)
  const inViewFired = useRef<boolean>(false)

  const inViewChanged = useCallback((inView: boolean) => {
    if (inView && !inViewFired.current) {
      // we only want to trigger in view event once but
      // we do want to keep notifying listeners it's changing
      inViewFired.current = true

      fireOfferListEvent(OfferListEvents.impression, {
        experience, position, key: EventDataKey.ProductImpression,
      })
    }
    onInViewChange(inView)
  }, [experience, fireOfferListEvent, onInViewChange, position])

  if (!experience) {
    return <HeroOfferLoadingTile />
  }

  return (
    <InView threshold={inViewThreshhold} rootMargin="0px" onChange={inViewChanged}>
      <HeroExperienceOfferTile className={className} experience={experience} />
    </InView>
  )
}

export default React.memo(OfferListExperienceTile)
